import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ApiService from "../../services/api-service";
import "./Admin.scss";

export default function AdminRoles() {
  const [adminUsers, setAdminUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState("info"); // success, error, info
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, action: null, email: null });

  // Load admin users on component mount
  useEffect(() => {
    loadAdminUsers();
  }, []);

  const loadAdminUsers = async () => {
    try {
      setStatusMsg("");
      const response = await ApiService.ADMIN_GET_ADMIN_USERS();
      if (response?.result?.adminUsers) {
        setAdminUsers(response.result.adminUsers);
      }
    } catch (error) {
      setStatusMsg("Failed to load admin users");
      setStatusType("error");
    }
  };

  const handleGrantAdmin = async () => {
    if (!newUserEmail.trim()) {
      setStatusMsg("Please enter a valid email address");
      setStatusType("error");
      return;
    }

    try {
      setLoading(true);
      setStatusMsg("");
      
      const response = await ApiService.ADMIN_MANAGE_ROLE({
        email: newUserEmail.trim(),
        action: "grant"
      });

      if (response?.result) {
        setStatusMsg(response.result.message || "Admin role granted successfully");
        setStatusType("success");
        setNewUserEmail("");
        await loadAdminUsers(); // Refresh the list
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to grant admin role";
      setStatusMsg(message);
      setStatusType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAdmin = async () => {
    const { email } = dialog;
    
    try {
      setLoading(true);
      setStatusMsg("");
      
      const response = await ApiService.ADMIN_MANAGE_ROLE({
        email: email,
        action: "revoke"
      });

      if (response?.result) {
        setStatusMsg(response.result.message || "Admin role revoked successfully");
        setStatusType("success");
        await loadAdminUsers(); // Refresh the list
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to revoke admin role";
      setStatusMsg(message);
      setStatusType("error");
    } finally {
      setLoading(false);
      setDialog({ open: false, action: null, email: null });
    }
  };

  return (
    <Box className="adminPortalContainer">
      <Typography className="adminHeader" variant="h4" gutterBottom>
        Admin Role Management
      </Typography>
      
      <Box className="tabPanel">
        <Typography className="sectionTitle">Grant Admin Access</Typography>
        <Box display="flex" alignItems="center" mb={3} gap={2}>
          <TextField
            className="textField"
            label="User Email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            placeholder="Enter email address"
            variant="outlined"
            fullWidth
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleGrantAdmin();
              }
            }}
          />
          <Button
            className="actionButton"
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={handleGrantAdmin}
            disabled={loading || !newUserEmail.trim()}
            sx={{ minWidth: 140, height: 56 }}
          >
            Grant Admin
          </Button>
        </Box>

        <Typography className="sectionTitle" sx={{ mt: 4, mb: 2 }}>
          Current Admin Users
        </Typography>
        
        <List className="listRoot">
          {adminUsers.map((user) => (
            <ListItem
              key={user._id}
              className="listItem"
              sx={{
                backgroundColor: '#23272f',
                border: '1px solid #444',
                borderRadius: 2,
                mb: 1,
                color: '#fff'
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" sx={{ color: '#fff' }}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    {user.email === 'chris@siki.io' && (
                      <Chip 
                        label="Owner" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                    {user.email}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                {user.email !== 'chris@siki.io' && (
                  <IconButton
                    edge="end"
                    aria-label="revoke"
                    onClick={() => setDialog({ 
                      open: true, 
                      action: "revoke", 
                      email: user.email 
                    })}
                    sx={{ color: '#ff4444' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {adminUsers.length === 0 && (
          <Typography sx={{ color: '#b0b0b0', textAlign: 'center', py: 4 }}>
            No admin users found
          </Typography>
        )}

        {statusMsg && (
          <Alert 
            severity={statusType} 
            sx={{ mt: 2, backgroundColor: statusType === 'error' ? '#2d1b1b' : '#1b2d1b' }}
          >
            {statusMsg}
          </Alert>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, action: null, email: null })}
        PaperProps={{
          sx: {
            backgroundColor: '#23272f',
            color: '#fff'
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>
          Revoke Admin Access
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b0b0b0' }}>
            Are you sure you want to revoke admin access for <strong>{dialog.email}</strong>?
            This user will no longer be able to access the admin portal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialog({ open: false, action: null, email: null })}
            sx={{ color: '#b0b0b0' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevokeAdmin}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Revoke Access
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}